import React from 'react'
import FareFamilySelectModal from 'components/Flights/V2/FareFamily/FareFamilySelectModal'
import type { ModalContextType } from 'contexts/ModalContext'
import { isResolvedFlightJourneyId } from './flightJourneyId'
import {
  cacheFareFamilySelection,
  getCachedFareFamilySelection,
  type FareFamilySelection,
} from './fareFamilySelectionCache'

interface FareFamilyModalResult {
  fareFamily?: App.FlightFareFamily
  returningFareFamily?: App.FlightFareFamily
  addToUserCart?: boolean
}

export type SelectFlightFareFamiliesResult =
  | { status: 'selected'; selection: FareFamilySelection }
  | { status: 'cancelled' }
  /** Only returned when `prompt` is false and no cached fare covers the journey. */
  | { status: 'prompt_required' }

export interface SelectFlightFareFamiliesArgs {
  departingJourney: App.JourneyV2
  returningJourney?: App.JourneyV2
  occupants: App.Occupants
  /** Fare families are fetched against this search; without it there is nothing to offer. */
  searchId: string | undefined
  sessionId: string | undefined
  showModal: ModalContextType
  /**
   * Multi-city legs have no fare to choose — they are priced and booked as the
   * whole itinerary, matching the standalone flights page.
   */
  isMultiCity?: boolean
  /**
   * When false, resolve from the fare cache only and report `prompt_required`
   * rather than opening the modal. Background conversions use this so they
   * never surface a modal the traveller did not ask for.
   */
  prompt?: boolean
  /**
   * Whether `journey.fareFamily` can be trusted as the answer to "does this
   * journey have fares to choose between". True for journeys mapped from a live
   * flight search, which carry it when there are fares and omit it when there
   * are none.
   *
   * Pass false for journeys mapped from the assistant's streamed chat payload
   * (`optionToJourney`), which never carries fare-family data at all — absence
   * there means "unknown", not "no fares".
   */
  journeysFromSearch?: boolean
}

/**
 * Whether there is a fare question worth putting to the traveller for a journey.
 *
 * A live search answers this outright via `journey.fareFamily`. Streamed chat
 * journeys never carry it, so we fall back to what the modal's own fetch needs:
 * a journey id that resolves inside the search that priced the fare. Without
 * that fallback every flight added straight from chat skipped the modal.
 */
function hasFareToChoose(journey: App.JourneyV2, journeysFromSearch: boolean): boolean {
  if (journey.fareFamily) return true
  if (journeysFromSearch) return false
  return isResolvedFlightJourneyId(journey.id, journey.bookingInfo?.journeyKey)
}

async function showFareFamilyModal(args: {
  journey: App.JourneyV2
  departingJourney?: App.JourneyV2
  departingFareFamily?: App.FlightFareFamily
  occupants: App.Occupants
  searchId: string
  sessionId: string | undefined
  showModal: ModalContextType
  journeysFromSearch: boolean
}): Promise<App.FlightFareFamily | undefined | 'cancelled'> {
  const { journey, departingJourney, departingFareFamily, occupants, searchId, sessionId, showModal, journeysFromSearch } = args
  if (!hasFareToChoose(journey, journeysFromSearch)) return undefined

  const result = await showModal<FareFamilyModalResult>(
    React.createElement(FareFamilySelectModal, {
      journey,
      departingJourney,
      departingFareFamily,
      occupancies: occupants,
      searchId,
      sessionId,
      relativePrice: journey.price.all.totalFare,
      isStandalone: true,
      // TripChat commits to one leg at a time - outbound, then inbound - so the fare question is
      // per leg no matter what the provider journey's `fareType` says. Re-running an expired
      // return search hands back journeys tagged `fareType: 'Return'`, and without this the modal
      // would read that tag as "pick a combined return fare" and render nothing.
      isSingleLegSelection: true,
    }),
  )

  if (!result) return 'cancelled'
  return result.fareFamily
}

/**
 * Runs the "Select a fare" modal for a chosen journey (and its return leg, when
 * there is one) and remembers the answer for the rest of the flight session.
 *
 * Shared by the two places a traveller commits to a flight in TripChat: adding
 * it to the trip from a search result, and converting a saved flight into a
 * cart item. Whichever runs first answers the fare question for both.
 */
export async function selectFlightFareFamilies({
  departingJourney,
  returningJourney,
  occupants,
  searchId,
  sessionId,
  showModal,
  isMultiCity = false,
  prompt = true,
  journeysFromSearch = true,
}: SelectFlightFareFamiliesArgs): Promise<SelectFlightFareFamiliesResult> {
  if (isMultiCity || !searchId) return { status: 'selected', selection: {} }

  const cacheKeyArgs = {
    departingJourneyKey: departingJourney.bookingInfo?.journeyKey,
    returningJourneyKey: returningJourney?.bookingInfo?.journeyKey,
    searchId,
  }

  const cached = getCachedFareFamilySelection(cacheKeyArgs)
  if (cached) return { status: 'selected', selection: cached }

  // The outbound fare may already have been chosen before the traveller moved
  // on to the return results. Reuse that choice when completing the pair.
  const cachedDeparting = returningJourney ? getCachedFareFamilySelection({
    departingJourneyKey: departingJourney.bookingInfo?.journeyKey,
    searchId,
  }) : undefined

  if (!prompt) {
    const isFareOutstanding = (!cachedDeparting && hasFareToChoose(departingJourney, journeysFromSearch)) ||
      (!!returningJourney && hasFareToChoose(returningJourney, journeysFromSearch))
    return isFareOutstanding ? { status: 'prompt_required' } : { status: 'selected', selection: cachedDeparting ?? {} }
  }

  const departingFareFamily = cachedDeparting ? cachedDeparting.departingFareFamily : await showFareFamilyModal({
    journey: departingJourney,
    occupants,
    searchId,
    sessionId,
    showModal,
    journeysFromSearch,
  })
  if (departingFareFamily === 'cancelled') return { status: 'cancelled' }

  const returningFareFamily = returningJourney ? await showFareFamilyModal({
    journey: returningJourney,
    // Hand the departing leg over only as a matched pair. The upsell endpoint reads a second
    // fare id arriving without a chosen departing fare family as "price both legs together" and
    // answers with the two legs paired, rather than the single-leg list this flow asks for. The
    // departing leg having had no fare to choose is the one way that happens here, so ask for
    // this leg on its own instead.
    departingJourney: departingFareFamily ? departingJourney : undefined,
    departingFareFamily,
    occupants,
    searchId,
    sessionId,
    showModal,
    journeysFromSearch,
  }) : undefined
  if (returningFareFamily === 'cancelled') return { status: 'cancelled' }

  const selection: FareFamilySelection = { departingFareFamily, returningFareFamily }
  cacheFareFamilySelection(cacheKeyArgs, selection)

  return { status: 'selected', selection }
}
